/**
 * 创建一个节流函数，限制函数在一定时间内只能执行一次
 * @param fn 需要节流的函数
 * @param wait 等待时间，单位毫秒
 * @param options 配置选项
 * @param options.leading 是否在延迟开始前调用，默认true
 * @param options.trailing 是否在延迟结束后调用，默认true
 * @returns 节流后的函数
 * @example
 * ```ts
 * const throttled = throttle(() => console.log('scroll'), 1000);
 * window.addEventListener('scroll', throttled);
 * ```
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {},
): T & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let previous = 0;
  const { leading = true, trailing = true } = options;

  function later(context: any, args: any[]) {
    previous = leading === false ? 0 : Date.now();
    timeout = null;
    fn.apply(context, args);
  }

  const throttled = function (this: any, ...args: any[]) {
    const now = Date.now();
    if (!previous && leading === false) {
      previous = now;
    }

    const remaining = wait - (now - previous);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      fn.apply(this, args);
    } else if (!timeout && trailing !== false) {
      timeout = setTimeout(() => later(this, args), remaining);
    }
  } as T & { cancel: () => void };

  throttled.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    previous = 0;
  };

  return throttled;
}
